import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FerramentaDeConteudo from "./FerramentaDeConteudo";
import MeiGestao from "./MeiGestao";
import MeiOcupacoes from "./MeiOcupacoes";
import { PortaGrande } from "../componentes/FerramentaUi";
import { listarClientes } from "../lib/meipro";

// O MEI Pro inteiro: a metade de CONTEÚDO (genérica, 000073/000076), a metade
// de GESTÃO (livro-caixa, 000077) e a consulta de OCUPAÇÕES (000086),
// despachadas pelo primeiro segmento depois do slug. `gestao` e `ocupacoes` são
// segmentos RESERVADOS desta ferramenta: uma seção de conteúdo criada com um
// desses endereços ficaria inalcançável — o runbook do MEI Pro avisa a equipe, e
// o conversor não gera esses slugs (os menus do Bubble não têm esses nomes).
export default function MeiPro() {
  const { "*": resto = "" } = useParams();
  const primeiro = resto.split("/").filter(Boolean)[0] ?? null;

  if (primeiro === "gestao") return <MeiGestao />;
  if (primeiro === "ocupacoes") return <MeiOcupacoes />;

  return <FerramentaDeConteudo card="mei-pro" painel={<PainelDeGestao />} />;
}

/**
 * As portas da gestão, no sumário do conteúdo. São painel e não itens do índice
 * porque gestão não é artigo: o índice lista o que se LÊ, e isto abre o que se
 * OPERA — misturar os dois foi o que o menu "Ferramentas" do Bubble fazia, com
 * cinco itens que não eram conteúdo no meio da navegação de leitura.
 *
 * Redesenho de 26/08: duas `PortaGrande` lado a lado (o card-porta da inicial),
 * e a da gestão traz um FATO vivo — quantos clientes o contador tem, e quantos
 * estão acima de 80% do teto. O número custa uma chamada a `mei_meus_clientes`,
 * que é a mesma que a tela de gestão faz; ele existe porque é a informação que
 * faz o contador ABRIR a gestão hoje em vez de amanhã.
 */
function PainelDeGestao() {
  const [fato, setFato] = useState<{ total: number; atencao: number } | null>(null);

  useEffect(() => {
    let vivo = true;
    void listarClientes().then((lista) => {
      if (!vivo || !lista) return;
      const atencao = lista.filter((c) => c.limite > 0 && c.receita_no_ano / c.limite >= 0.8).length;
      setFato({ total: lista.length, atencao });
    });
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2">
      <PortaGrande
        para="/ferramenta/mei-pro/gestao"
        titulo="Gestão de clientes"
        descricao="Cadastre os seus clientes MEI, escriture o livro-caixa e acompanhe o faturamento de cada um contra o teto do ano."
        cta="Gerenciar clientes"
        fato={
          fato && fato.total > 0
            ? fato.atencao > 0
              ? `${fato.total} ${fato.total === 1 ? "cliente" : "clientes"} · ${fato.atencao} acima de 80% do teto`
              : `${fato.total} ${fato.total === 1 ? "cliente" : "clientes"}`
            : undefined
        }
        atencao={Boolean(fato && fato.atencao > 0)}
        icone={<IconeLivro />}
        i={0}
      />
      <PortaGrande
        para="/ferramenta/mei-pro/ocupacoes"
        titulo="Atividades permitidas e vedadas"
        descricao="Procure pelo nome da atividade ou pelo CNAE e veja se ela pode ser MEI, e se paga ICMS ou ISS."
        cta="Consultar atividade"
        icone={<IconeLupa />}
        i={1}
      />
    </div>
  );
}

function IconeLivro() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-6" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M9 7h7M9 11h7" />
    </svg>
  );
}

function IconeLupa() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-6" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
